/**
 * Marcas / brandings (listado paginado). Mismo envelope que el resto del API.
 *
 * @typedef {{ id: string, name: string, costCenter?: string, createdAt?: string, updatedAt?: string }} BrandingDto
 */
import axiosInstance from '../utils/axiosInstance';

/** Tamaño de página para traer todas las marcas en pocas peticiones (`brandings?page=1&size=100`). */
export const BRANDINGS_FETCH_PAGE_SIZE = 100;

/**
 * Obtiene todos los brandings paginando hasta cubrir `totalCount` (o hasta una página parcial).
 *
 * @param {{ signal?: AbortSignal, pageSize?: number }} [options]
 * @returns {Promise<BrandingDto[]>}
 */
export async function fetchAllBrandings(options = {}) {
  const { signal, pageSize = BRANDINGS_FETCH_PAGE_SIZE } = options;
  /** @type {BrandingDto[]} */
  const all = [];
  let page = 1;

  for (;;) {
    const { data } = await axiosInstance.get(`brandings?page=${page}&size=${pageSize}`, {
      signal
    });

    const records = data?.records;
    if (!records || records.code !== 'SUCCESS') {
      const msg = records?.message;
      throw new Error(typeof msg === 'string' && msg.trim() ? msg : 'Error al obtener marcas (brandings)');
    }

    const batch = Array.isArray(records.data) ? records.data : [];
    all.push(...batch);

    const total =
      typeof data?.totalCount === 'number' && Number.isFinite(data.totalCount) && data.totalCount >= 0
        ? data.totalCount
        : null;

    if (total != null && all.length >= total) break;
    if (batch.length === 0) break;
    if (batch.length < pageSize) break;

    page += 1;
    if (page > 500) break;
  }

  all.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
  return all;
}
